import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";

// Color design tokens updated for a bold, modern, and vibrant SaaS look
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        // Crisp, neutral grays for high-contrast typography
        gray: {
          100: "#f8fafc",
          200: "#f1f5f9",
          300: "#e2e8f0",
          400: "#cbd5e1",
          500: "#94a3b8",
          600: "#64748b",
          700: "#475569",
          800: "#334155",
          900: "#1e293b",
        },
        // Deep, rich navy/slate tones for backgrounds and surfaces
        primary: {
          100: "#e2e8f0",
          200: "#cbd5e1",
          300: "#94a3b8",
          400: "#1e293b", // Surface / Card Background
          500: "#0f172a", // Main App Background
          600: "#0b1120",
          700: "#080c16",
          800: "#05080f",
          900: "#020408",
        },
        // Vibrant Emerald/Teal for success states and accents
        greenAccent: {
          100: "#ccfbf1",
          200: "#99f6e4",
          300: "#5eead4",
          400: "#2dd4bf",
          500: "#14b8a6", // Main Success
          600: "#0d9488",
          700: "#0f766e",
          800: "#115e59",
          900: "#134e4a",
        },
        // Bold Rose/Red for destructive actions and errors
        redAccent: {
          100: "#ffe4e6",
          200: "#fecdd3",
          300: "#fda4af",
          400: "#fb7185",
          500: "#f43f5e", // Main Error
          600: "#e11d48",
          700: "#be123c",
          800: "#9f1239",
          900: "#881337",
        },
        // Bright Amber for warnings and highlights
        yellowAccent: {
          100: "#fef3c7",
          200: "#fde68a",
          300: "#fcd34d",
          400: "#fbbf24",
          500: "#f59e0b", // Main Warning
          600: "#d97706",
          700: "#b45309",
          800: "#92400e",
          900: "#78350f",
        },
        // Electric Indigo/Blue for primary actions and active states
        blueAccent: {
          100: "#e0e7ff",
          200: "#c7d2fe",
          300: "#a5b4fc",
          400: "#818cf8",
          500: "#6366f1", // Main Brand Color
          600: "#4f46e5",
          700: "#4338ca",
          800: "#3730a3",
          900: "#312e81",
        },
      }
    : {
        // Light mode reverses the gray scale for text
        gray: {
          100: "#1e293b",
          200: "#334155",
          300: "#475569",
          400: "#64748b",
          500: "#94a3b8",
          600: "#cbd5e1",
          700: "#e2e8f0",
          800: "#f1f5f9",
          900: "#f8fafc",
        },
        // Light mode surfaces and backgrounds
        primary: {
          100: "#0f172a",
          200: "#1e293b",
          300: "#334155",
          400: "#ffffff", // Surface / Card Background (Clean White)
          500: "#f8fafc", // Main App Background (Off-White/Gray)
          600: "#e2e8f0",
          700: "#cbd5e1",
          800: "#94a3b8",
          900: "#64748b",
        },
        // Light mode accents (slightly deeper for contrast on light backgrounds)
        greenAccent: {
          100: "#134e4a",
          200: "#115e59",
          300: "#0f766e",
          400: "#0d9488",
          500: "#059669", // Deeper Emerald for light mode
          600: "#10b981",
          700: "#34d399",
          800: "#6ee7b7",
          900: "#a7f3d0",
        },
        redAccent: {
          100: "#881337",
          200: "#9f1239",
          300: "#be123c",
          400: "#e11d48",
          500: "#e11d48", // Deeper Rose for light mode
          600: "#f43f5e",
          700: "#fb7185",
          800: "#fda4af",
          900: "#fecdd3",
        },
        yellowAccent: {
          100: "#78350f",
          200: "#92400e",
          300: "#b45309",
          400: "#d97706",
          500: "#d97706", // Deeper Amber for light mode
          600: "#f59e0b",
          700: "#fbbf24",
          800: "#fcd34d",
          900: "#fde68a",
        },
        blueAccent: {
          100: "#312e81",
          200: "#3730a3",
          300: "#4338ca",
          400: "#4f46e5",
          500: "#4f46e5", // Deeper Indigo for light mode contrast
          600: "#6366f1",
          700: "#818cf8",
          800: "#a5b4fc",
          900: "#c7d2fe",
        },
      }),
});

// MUI Theme Settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);

  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: {
              main: colors.blueAccent[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.gray[700],
              main: colors.gray[500],
              light: colors.gray[100],
            },
            background: {
              default: colors.primary[500], // #0f172a
              paper: colors.primary[400], // #1e293b
            },
          }
        : {
            primary: {
              main: colors.blueAccent[500],
            },
            secondary: {
              main: colors.greenAccent[500],
            },
            neutral: {
              dark: colors.gray[700],
              main: colors.gray[500],
              light: colors.gray[100],
            },
            background: {
              default: colors.primary[500], // #f8fafc
              paper: colors.primary[400], // #ffffff
            },
          }),
    },
    typography: {
      fontFamily: ["Inter", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontSize: 32,
        fontWeight: 700,
      },
      h2: {
        fontSize: 24,
        fontWeight: 700,
      },
      h3: {
        fontSize: 20,
        fontWeight: 600,
      },
      h4: {
        fontSize: 18,
        fontWeight: 600,
      },
      h5: {
        fontSize: 16,
        fontWeight: 500,
      },
      h6: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
  };
};

// Context for color mode
export const ColorModeContext = createContext({
  themePreference: "system",
  setThemePreference: () => {},
});

export const useMode = () => {
  // State for the user's explicitly chosen preference (light, dark, system)
  const [themePreference, setThemePreferenceState] = useState(() => {
    const savedMode = localStorage.getItem("themePreference");
    return savedMode ? savedMode : "system";
  });

  // State for the resolved MUI theme mode (strictly "light" or "dark")
  const [actualMode, setActualMode] = useState(() => {
    const savedMode = localStorage.getItem("themePreference") || "system";
    if (savedMode === "system") {
      if (typeof window !== "undefined" && window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light";
      }
      return "dark"; // Fallback
    }
    return savedMode;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    // Determine the actual UI mode to render based on preference
    const resolveMode = () => {
      if (themePreference === "system") {
        setActualMode(mediaQuery.matches ? "dark" : "light");
      } else {
        setActualMode(themePreference);
      }
    };

    resolveMode();

    // Event listener for OS level theme changes (only active when preference is 'system')
    const handleChange = (e) => {
      if (themePreference === "system") {
        setActualMode(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [themePreference]);

  const colorMode = useMemo(
    () => ({
      themePreference,
      setThemePreference: (newMode) => {
        localStorage.setItem("themePreference", newMode);
        setThemePreferenceState(newMode);
      },
    }),
    [themePreference],
  );

  const theme = useMemo(
    () => createTheme(themeSettings(actualMode)),
    [actualMode],
  );

  return [theme, colorMode];
};
